import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Link as RouterLink } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ArrowDropDown } from '@mui/icons-material';
import { useRootStore } from '../../../../stores/rootStore';
import { SoldMetrics } from './SoldMetrics';
import { ForSaleMetrics } from './ForSaleMetrics';
import { PropertyMetrics } from './PropertyMetrics';
import { RentalMetrics } from './RentalMetrics';
import { ValuationMetrics } from './ValuationMetrics';

const SeriesRequest = () => (
  <>
    <Typography variant="body2">
      Please see the example request for Market Trends below:
    </Typography>

    <br />

    <SyntaxHighlighter language="json" style={a11yDark} wrapLongLines>
      {JSON.stringify(
        {
          locationId: '14489',
          locationTypeId: 8,
          propertyTypeId: 1,
          fromDate: '2019-02-01',
          toDate: '2020-02-01',
          metricTypeId: 21,
          interval: 1,
        },
        null,
        2,
      )}
    </SyntaxHighlighter>

    <br />

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ArrowDropDown />}>
        <Typography fontWeight={600}>Request Parameters</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Required?</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Example</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>fromDate</TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell>
                  The start date for the data required in the series in format
                  YYYY-MM-DD.
                  <br />
                  This is mandatory if toDate is entered. Defaults to return
                  most recent available data.
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "fromDate": "2012-02-29"
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>toDate</TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell>
                  The end date for the data required in the series in format
                  YYYY-MM-DD.
                  <br />
                  <br />
                  Defaults to fromDate + 12 months (or today's date whichever is
                  lesser).
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "toDate": "2013-03-31"
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>interval</TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  Determines the intervals for the data returned and are
                  calculated from the recent date which has metric values in a
                  requested range. In case of metricTypeGroupId the data
                  returned and calculated from any metric type in group having
                  most recent date which has metric value in a requested date
                  range.
                  <br />
                  <br />
                  For example, 1 = monthly, 2 = every two months, 3 = quarterly,
                  6 = half yearly, 12 = yearly, 24= 2 years, etc.
                  <br />
                  <br />
                  Defaults to 1.
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "interval": 1
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>locationId</TableCell>
                <TableCell>Yes</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  A unique identifier for the location in a given location type.
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "locationId": 7926
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  locationTypeId
                </TableCell>
                <TableCell>Yes</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  A unique identifier for the location or area type.
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>2 = Country (AU/NZ)</li>
                    <li>3 = Council Area (AU only)</li>
                    <li>4 = Postcode (AU only)</li>
                    <li>7 = State (AU only)</li>
                    <li>8 = Locality (Suburb) (AU/NZ)</li>
                    <li>9 = Territorial Authority (NZ only)</li>
                  </ul>
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "locationTypeId": 8
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>chartType</TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  The chartType can override any chartType from sN.cTId and
                  default chartType from metric type group. For example, the
                  default chartType id for metricTypeGroup 1 is Column, setting
                  chartTypeId=5 will override chartType to Pie chart globally.
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>1 = SPLINE</li>
                    <li>2 = LINE</li>
                    <li>3 = COLUMN</li>
                    <li>4 = AREA</li>
                    <li>5 = PIE</li>
                    <li>6 = BAR</li>
                  </ul>
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "chartType": 5
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  metricTypeGroupId
                </TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  Returns all metric types for a requested metric type group.
                  <br />
                  <br />
                  Only required if metricTypeId is not passed and cannot be
                  passed in conjunction with metricTypeId.
                  <br />
                  <br />A unique identifier for the metric group; accepted
                  values:
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>1 = Number Sold by Price (12 months)</li>
                    <li>2 = Median Price by Price (12 months)</li>
                    <li>3 = Percentile Sale Price (12 months)</li>
                  </ul>
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "metricTypeGroupId": 1
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  metricTypeId
                </TableCell>
                <TableCell>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  A unique identifier for the metric.
                  <br />
                  <br />
                  Only required if metricGroupId is not passed and cannot be
                  passed in conjunction with metricGroupId.
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "metricTypeId": 21
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  propertyTypeId
                </TableCell>
                <TableCell>Yes</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Integer</TableCell>
                <TableCell>
                  A unique identifier for the type of property.
                  <br />
                  <br />
                  Australia & New Zealand property types:
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>1 = Houses</li>
                    <li>3 = Land</li>
                  </ul>
                  <br />
                  Australia specific property types:
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>2 = Units</li>
                  </ul>
                  <br />
                  New Zealand specific property types:
                  <ul style={{ lineHeight: 1.5 }}>
                    <li>4 = Apartments</li>
                    <li>5 = Flats</li>
                    <li>
                      6 = Residential dwellings (Houses, Apartments, Flats)
                    </li>
                  </ul>
                </TableCell>
                <TableCell>
                  <SyntaxHighlighter language="json" style={a11yDark}>
                    "propertyTypeId": 1
                  </SyntaxHighlighter>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  </>
);

export const MetricTypesMarketTrends = () => {
  const country = useRootStore(state => state.country);

  return (
    <>
      <Typography
        id="metric-types-market-trends"
        variant="h5"
        fontWeight={600}
        my={0.5}
      >
        Market Trends
      </Typography>

      <br />

      <Typography variant="body2">
        Market Trends is an essential tool for anyone that wants to truly
        understand the housing market at a micro level across Australia and New
        Zealand, including any business involved in the housing market.
        <br />
        <br />
        Our Market Trends data is available via our{' '}
        <Link
          component={RouterLink}
          to={`/apis/cotality-apis/market-insights-${country}#/statistics`}
        >
          Statistics
        </Link>{' '}
        service and{' '}
        <Link
          component={RouterLink}
          to={`/apis/cotality-apis/market-insights-${country}#/charts`}
        >
          Charts
        </Link>{' '}
        service. These services allow you to view time series (or trend) data
        for one or more metrics, locations and property types in order to
        understand the trend and historical performance of the metric.
      </Typography>

      <br />

      <ul>
        <li>
          <Link href="#sold-metrics">Sold</Link>
        </li>
        <li>
          <Link href="#for-sale-metrics">For Sale</Link>
        </li>
        <li>
          <Link href="#rental-metrics">Rental</Link>
        </li>
        <li>
          <Link href="#valuation-metrics">Valuation</Link>
        </li>
        <li>
          <Link href="#property-metrics">Property</Link>
        </li>
      </ul>

      <br />

      <SeriesRequest />

      <br />
      <br />

      <SoldMetrics />

      <br />
      <br />

      <ForSaleMetrics />

      <br />
      <br />

      <RentalMetrics />

      <br />
      <br />

      <ValuationMetrics />

      <br />
      <br />

      <PropertyMetrics />
    </>
  );
};
